/**
 * Standalone (full-page) layout for question and composer pages.
 *
 * Implements a responsive CSS grid with single-column and two-column
 * breakpoints. Wraps content to provide the root scroll container. The
 * two-column mode activates when a `rightRail` prop is passed or when
 * `forceRailLayout` is set.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { spacings } from 'component/constants';
import { SCROLL_CONTAINER_ID } from 'component/screen';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { LAYOUT_SINGLE_COLUMN, LAYOUT_TWO_COLUMN, useIsDesktopBreakpoint } from 'util/layout';

import { StandaloneQuestionFooter } from './standalone-footer';
import { StandaloneQuestionHeader } from './standalone-header';

export type StandaloneLayoutProps = {
    /**
     * Header content. When provided, renders inside a full-bleed highlight
     * banner.
     */
    header?: React.ReactNode;
    /** Main content placed in the content column. */
    children: React.ReactNode;
    /** Rendered above the grid (full-width). */
    topBanners?: React.ReactNode;
    /** Rendered below main content. */
    bottomWidgets?: React.ReactNode;
    /**
     * Right rail content. When present on desktop, enables two-column mode with
     * divider.
     */
    rightRail?: React.ReactNode;
    /**
     * Use two-column grid on desktop even without right rail content. Enables
     * the full-width header spanning to rail-end.
     */
    wideHeaderOnDesktop?: boolean;
    /** Header banner background color. Defaults to colorSurfaceHighlight. */
    headerBackgroundColor?: string;
    /** Footer content. */
    footer?: React.ReactNode;
    withHeader?: boolean;
};

export function StandaloneLayout({
    header,
    children,
    topBanners,
    bottomWidgets,
    rightRail,
    wideHeaderOnDesktop = false,
    footer,
    headerBackgroundColor,
}: StandaloneLayoutProps) {
    const isDesktop = useIsDesktopBreakpoint();
    const showRightRail = isDesktop && !!rightRail;
    const useWideGrid = showRightRail || (isDesktop && wideHeaderOnDesktop);
    const layoutColumns = useWideGrid ? LAYOUT_TWO_COLUMN : LAYOUT_SINGLE_COLUMN;
    const s = useStyles();
    const { colors } = useTheme();

    return (
        <View style={s.outer}>
            {/* nativeID anchors getScrollContainer(); without it, popovers
                portalled here lose scroll-tracking and pin to document.body. */}
            <ScrollView contentContainerStyle={{ minHeight: '100%' }} nativeID={SCROLL_CONTAINER_ID} style={s.scroller}>
                {topBanners}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: layoutColumns.join(' '),
                        gridTemplateRows: 'auto 1fr auto',
                        flex: 1,
                    }}
                >
                    {header && (
                        <StandaloneQuestionHeader backgroundColor={headerBackgroundColor} fullWidth={useWideGrid}>
                            {header}
                        </StandaloneQuestionHeader>
                    )}

                    {showRightRail ? (
                        <>
                            <div style={{ gridColumn: 'content-start / content-end', gridRowEnd: '3', marginBottom: spacings.xl5 }}>
                                {children}
                                {bottomWidgets}
                            </div>

                            <div
                                style={{
                                    gridColumn: 'divider-start / divider-end',
                                    backgroundColor: colors.colorBorderPrimary,
                                    marginTop: spacings.xl2,
                                    marginBottom: spacings.xl5,
                                }}
                            />

                            <div
                                style={{
                                    gridColumn: 'rail-start / rail-end',
                                    paddingInline: spacings.lg,
                                    paddingTop: spacings.xl2,
                                    gap: spacings.lg,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {rightRail}
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ gridColumn: 'content-start / content-end' }}>{children}</div>
                            {bottomWidgets && (
                                <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>{bottomWidgets}</div>
                            )}
                        </>
                    )}
                    {footer && <StandaloneQuestionFooter>{footer}</StandaloneQuestionFooter>}
                </div>
            </ScrollView>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: {
        flex: 1,
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
    scroller: {
        flex: 1,
    },
}));
